import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../Auth/AuthContext";
import { SettingsContext } from "../Settings/SettingsContext";
import { SocketContext } from "../Socket/SocketContext";
import { CALLS_MENU_KEY, canShowMenu } from "../../helpers/menuAccess";
import { shouldAlertForTicket } from "../../helpers/canAccessTicket";
import ringtoneSound from "../../assets/phone-alert-marimba-bubble-om-fx-1-00-01.mp3";
import {
  applyAnswer,
  closeCallPeer,
  createAnswerForOffer,
  createCallPeer,
  createOffer,
  MicrophoneUnavailableError,
  setMicrophoneMuted,
} from "../../services/calls/webRtcCall";

// Ligação pelo WhatsApp (hoje só Cloud API). Uma ligação por vez por aba:
//   saída:   dialing → ringing → connected → ended
//   entrada: (lista `incomingCalls` tocando) → answering → connected → ended
// A sinalização chega por `company-{id}-call`. Cada tentativa tem um
// `attemptToken` que vai ao backend e volta nos eventos: é assim que só a aba
// que ligou/atendeu aplica o SDP (o mesmo usuário pode ter várias abas abertas).

const DEFAULT_ICE_SERVERS = [{ urls: "stun:stun.l.google.com:19302" }];
// Quanto tempo o resumo "Ligação encerrada" fica no painel.
const ENDED_DISPLAY_MS = 4000;
// A Meta desiste da ligação recebida antes disso; é só a rede de segurança do aviso.
const INCOMING_TIMEOUT_MS = 90 * 1000;

const newAttemptToken = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const contactFromTicket = (ticket) => ({
  id: ticket?.contact?.id,
  name: ticket?.contact?.name,
  number: ticket?.contact?.number,
  profilePicUrl: ticket?.contact?.profilePicUrl,
});

const toastMicrophoneError = (err) => {
  const key = err?.message === "insecure-context" ? "calls.errors.insecureContext" : "calls.errors.microphone";
  toast.error(i18n.t(key), { toastId: "call-microphone" });
};

export const CallContext = createContext({
  call: null,
  incomingCalls: [],
  permissionPrompt: null,
  canUseCalls: false,
  startCallFromTicket: () => {},
  startCallFromContact: () => {},
  answerIncoming: () => {},
  ignoreIncoming: () => {},
  hangUp: () => {},
  toggleMute: () => {},
  requestPermission: () => {},
  dialFromPrompt: () => {},
  endStaleCall: () => {},
  closePermissionPrompt: () => {},
});

export const useCall = () => useContext(CallContext);

export const CallProvider = ({ children }) => {
  const { user, menus } = useContext(AuthContext);
  const { connectionScopeEnabled } = useContext(SettingsContext);
  const socketManager = useContext(SocketContext);

  const [call, setCall] = useState(null);
  const [incomingCalls, setIncomingCalls] = useState([]);
  const [permissionPrompt, setPermissionPrompt] = useState(null);

  // Espelhos para os handlers do socket e do WebRTC, que vivem fora do render.
  const callRef = useRef(null);
  const peerRef = useRef({ pc: null, localStream: null });
  const remoteAudioRef = useRef(null);
  const ringtoneRef = useRef(null);
  const iceServersRef = useRef(null);
  const endedTimerRef = useRef(null);
  const hangUpRef = useRef(null);
  const handleEventRef = useRef(null);

  const canUseCalls = canShowMenu(user, menus, CALLS_MENU_KEY);

  // callRef é a fonte da verdade e muda na hora: dentro de um clique o React só
  // aplicaria o setState no próximo render, e as checagens de "tentativa ainda
  // ativa" (depois de cada await) leriam a ligação anterior.
  const updateCall = useCallback((next) => {
    const value = typeof next === "function" ? next(callRef.current) : next;
    callRef.current = value;
    setCall(value);
  }, []);

  const isAttemptActive = (attemptToken) => {
    const current = callRef.current;
    return Boolean(current && current.attemptToken === attemptToken && current.phase !== "ended");
  };

  const releasePeer = useCallback(() => {
    const { pc, localStream } = peerRef.current;
    closeCallPeer(pc, localStream);
    peerRef.current = { pc: null, localStream: null };
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
  }, []);

  const finishCall = useCallback(
    (reason, extra = {}) => {
      releasePeer();
      clearTimeout(endedTimerRef.current);
      updateCall((prev) => (prev ? { ...prev, ...extra, phase: "ended", endedReason: reason } : prev));
      endedTimerRef.current = setTimeout(() => updateCall(null), ENDED_DISPLAY_MS);
    },
    [releasePeer, updateCall]
  );

  const getIceServers = async () => {
    if (iceServersRef.current) return iceServersRef.current;
    try {
      const { data } = await api.get("/calls/ice-servers");
      iceServersRef.current = data?.iceServers?.length ? data.iceServers : DEFAULT_ICE_SERVERS;
    } catch (err) {
      iceServersRef.current = DEFAULT_ICE_SERVERS;
    }
    return iceServersRef.current;
  };

  const openPeer = async () => {
    const iceServers = await getIceServers();
    const peer = await createCallPeer({
      iceServers,
      onRemoteStream: (stream) => {
        const audio = remoteAudioRef.current;
        if (!audio) return;
        audio.srcObject = stream;
        audio.play?.().catch(() => {});
      },
      onConnectionState: (state) => {
        if (state === "failed") hangUpRef.current?.("mediaFailed");
      },
    });
    peerRef.current = peer;
    return peer;
  };

  const hangUp = useCallback(
    async (reason = "hangup") => {
      const current = callRef.current;
      if (!current || current.phase === "ended") return;

      finishCall(reason);
      if (current.callId) {
        try {
          await api.post("/calls/terminate", { callId: current.callId });
        } catch (err) {
          // O contato pode ter desligado antes: a ligação já acabou.
        }
      }
    },
    [finishCall]
  );

  useEffect(() => {
    hangUpRef.current = hangUp;
  }, [hangUp]);

  // `prepareTicket(ticket)`: quem liga da tela do chamado assume o chamado antes
  // (mesma regra do envio de mensagem). Roda só depois do microfone liberado,
  // para uma ligação que nem chega a sair não mexer no chamado. Devolve o chamado
  // em que a ligação sai (pode ser outro do mesmo contato) ou null para desistir.
  const dial = async (ticket, prepareTicket) => {
    const attemptToken = newAttemptToken();
    clearTimeout(endedTimerRef.current);
    updateCall({
      phase: "dialing",
      direction: "outbound",
      attemptToken,
      callId: null,
      ticketId: ticket.id,
      ticketUuid: ticket.uuid,
      contact: contactFromTicket(ticket),
      muted: false,
      startedAt: null,
    });

    let startedCallId = null;
    let target = ticket;
    try {
      const { pc } = await openPeer();

      if (prepareTicket) {
        target = await prepareTicket(ticket);
        if (!isAttemptActive(attemptToken)) return;
        if (!target) {
          releasePeer();
          updateCall(null);
          return;
        }
        if (Number(target.id) !== Number(ticket.id)) {
          updateCall((prev) =>
            prev
              ? { ...prev, ticketId: target.id, ticketUuid: target.uuid, contact: contactFromTicket(target) }
              : prev
          );
        }
      }

      const sdp = await createOffer(pc);
      if (!isAttemptActive(attemptToken)) return;

      const { data } = await api.post(`/calls/ticket/${target.id}/start`, { sdp, clientToken: attemptToken });
      startedCallId = data.callId;

      // Desligou enquanto o POST estava em voo: encerra o que a Meta já iniciou.
      if (!isAttemptActive(attemptToken)) {
        api.post("/calls/terminate", { callId: startedCallId }).catch(() => {});
        return;
      }

      updateCall((prev) => (prev ? { ...prev, callId: prev.callId || startedCallId } : prev));
    } catch (err) {
      if (!isAttemptActive(attemptToken)) return;
      releasePeer();
      updateCall(null);

      if (err instanceof MicrophoneUnavailableError) {
        toastMicrophoneError(err);
        return;
      }
      if (err?.response?.data?.error === "ERR_CALL_NO_PERMISSION") {
        // A consulta de permissão falhou ou ficou velha: oferece pedir de novo.
        // Sem prepareTicket: o chamado já foi assumido nesta tentativa.
        startCallFromTicketRef.current?.(target, { forcePrompt: true });
        return;
      }
      toastError(err);
    }
  };

  const startCallFromTicket = async (ticket, { forcePrompt = false, prepareTicket } = {}) => {
    if (!ticket?.id) return;
    if (callRef.current && callRef.current.phase !== "ended") {
      toast.info(i18n.t("calls.errors.alreadyInCall"), { toastId: "call-busy" });
      return;
    }

    try {
      const { data: capabilities } = await api.get(`/calls/ticket/${ticket.id}/capabilities`);
      const permission = capabilities?.permission;
      const blocked =
        forcePrompt ||
        Boolean(capabilities?.activeCallId) ||
        capabilities?.callingEnabled === false ||
        (permission && !permission.canStartCall);

      if (blocked) {
        setPermissionPrompt({ ticket, capabilities, prepareTicket, requested: false, loading: false });
        return;
      }

      await dial(ticket, prepareTicket);
    } catch (err) {
      toastError(err);
    }
  };

  const startCallFromTicketRef = useRef(null);
  startCallFromTicketRef.current = startCallFromTicket;

  // Contato sem chamado (aba Contatos): não há chamado para consultar a permissão
  // antes. `prepareTicket()` cria/assume o chamado depois do microfone liberado e
  // devolve o chamado (ou null para desistir); sem permissão, o POST de início
  // dá ERR_CALL_NO_PERMISSION e o aviso de permissão abre já nesse chamado.
  const startCallFromContact = async (contact, { prepareTicket } = {}) => {
    if (!contact?.id || !prepareTicket) return;
    if (callRef.current && callRef.current.phase !== "ended") {
      toast.info(i18n.t("calls.errors.alreadyInCall"), { toastId: "call-busy" });
      return;
    }

    await dial({ id: null, uuid: null, contact }, prepareTicket);
  };

  const answerIncoming = async (incoming) => {
    if (callRef.current && callRef.current.phase !== "ended") return;

    const attemptToken = newAttemptToken();
    clearTimeout(endedTimerRef.current);
    setIncomingCalls((prev) => prev.filter((item) => item.callId !== incoming.callId));
    updateCall({
      phase: "answering",
      direction: "inbound",
      attemptToken,
      callId: incoming.callId,
      ticketId: incoming.ticketId,
      ticketUuid: incoming.ticket?.uuid,
      contact: incoming.contact,
      muted: false,
      startedAt: null,
    });

    let claimed = false;
    try {
      // Microfone antes de pegar a ligação: se for negado, outro atendente ainda atende.
      const { pc } = await openPeer();
      const { data } = await api.post("/calls/answer", { callId: incoming.callId, clientToken: attemptToken });
      claimed = true;

      const sdp = await createAnswerForOffer(pc, data.sdpOffer);
      if (!isAttemptActive(attemptToken)) return;

      await api.post("/calls/accept", { callId: incoming.callId, sdp });
      if (!isAttemptActive(attemptToken)) return;
      updateCall((prev) => (prev ? { ...prev, phase: "connected", startedAt: Date.now() } : prev));
    } catch (err) {
      if (!isAttemptActive(attemptToken)) return;
      releasePeer();
      updateCall(null);
      if (claimed) {
        api.post("/calls/terminate", { callId: incoming.callId }).catch(() => {});
      }

      if (err instanceof MicrophoneUnavailableError) {
        toastMicrophoneError(err);
        return;
      }
      toastError(err);
    }
  };

  const ignoreIncoming = (callId) => {
    setIncomingCalls((prev) => prev.filter((item) => item.callId !== callId));
  };

  const toggleMute = () => {
    const muted = !callRef.current?.muted;
    setMicrophoneMuted(peerRef.current.localStream, muted);
    updateCall((prev) => (prev ? { ...prev, muted } : prev));
  };

  const requestPermission = async () => {
    const ticket = permissionPrompt?.ticket;
    if (!ticket) return;

    setPermissionPrompt((prev) => (prev ? { ...prev, loading: true } : prev));
    try {
      await api.post(`/calls/ticket/${ticket.id}/permission-request`);
      toast.success(i18n.t("calls.permission.requested"));
      setPermissionPrompt((prev) => (prev ? { ...prev, loading: false, requested: true } : prev));
    } catch (err) {
      setPermissionPrompt((prev) => (prev ? { ...prev, loading: false } : prev));
      toastError(err);
    }
  };

  const dialFromPrompt = async () => {
    const ticket = permissionPrompt?.ticket;
    const prepareTicket = permissionPrompt?.prepareTicket;
    setPermissionPrompt(null);
    if (ticket) await dial(ticket, prepareTicket);
  };

  // Sessão presa de uma ligação anterior (aba fechada no meio da ligação, por exemplo).
  const endStaleCall = async () => {
    const prompt = permissionPrompt;
    const callId = prompt?.capabilities?.activeCallId;
    if (!callId) return;

    setPermissionPrompt((prev) => (prev ? { ...prev, loading: true } : prev));
    try {
      await api.post("/calls/terminate", { callId });
    } catch (err) {
      // já encerrada
    }
    setPermissionPrompt(null);
    await startCallFromTicket(prompt.ticket, { prepareTicket: prompt.prepareTicket });
  };

  const closePermissionPrompt = () => setPermissionPrompt(null);

  // Sinalização vinda do backend. A função muda a cada render (lê estado atual);
  // o listener do socket chama sempre a versão mais nova pela ref.
  handleEventRef.current = (data) => {
    if (!data?.action) return;
    const current = callRef.current;
    const isCurrentCall =
      Boolean(current) &&
      current.phase !== "ended" &&
      ((data.callId && data.callId === current.callId) ||
        (data.clientToken && data.clientToken === current.attemptToken));

    switch (data.action) {
      case "answer": {
        if (!isCurrentCall || data.clientToken !== current.attemptToken) return;
        const { pc } = peerRef.current;
        if (!pc || pc.signalingState !== "have-local-offer" || !data.sdp) return;
        updateCall((prev) => (prev ? { ...prev, callId: prev.callId || data.callId } : prev));
        applyAnswer(pc, data.sdp).catch((err) => {
          console.error("[Calls] SDP de resposta recusado pelo navegador", err);
          hangUpRef.current?.("mediaFailed");
        });
        return;
      }

      case "status": {
        if (!isCurrentCall) return;
        if (data.status === "ringing") {
          updateCall((prev) => (prev && prev.phase === "dialing" ? { ...prev, phase: "ringing" } : prev));
        } else if (data.status === "accepted") {
          updateCall((prev) => (prev ? { ...prev, phase: "connected", startedAt: prev.startedAt || Date.now() } : prev));
        } else if (data.status === "rejected") {
          finishCall("rejected");
        }
        return;
      }

      case "ended": {
        setIncomingCalls((prev) => prev.filter((item) => item.callId !== data.callId));
        if (!isCurrentCall) return;
        finishCall(data.reason === "local" ? "hangup" : data.status || "ended", {
          durationSeconds: data.durationSeconds || null,
        });
        return;
      }

      case "incoming": {
        if (!canUseCalls) return;
        if (!shouldAlertForTicket(user, data.ticket, connectionScopeEnabled)) return;
        setIncomingCalls((prev) =>
          prev.some((item) => item.callId === data.callId) ? prev : [...prev, { ...data, receivedAt: Date.now() }]
        );
        return;
      }

      case "claimed": {
        // Esta aba atendeu: o callId já é o da ligação atual.
        if (current?.callId === data.callId) return;
        setIncomingCalls((prev) => prev.filter((item) => item.callId !== data.callId));
        return;
      }

      case "permission": {
        setPermissionPrompt((prev) => {
          if (!prev || Number(prev.ticket?.id) !== Number(data.ticketId)) return prev;
          const permission = {
            ...(prev.capabilities?.permission || {}),
            status: data.status,
            expiresAt: data.expiresAt || null,
            canStartCall: data.status !== "no_permission",
          };
          return { ...prev, capabilities: { ...prev.capabilities, permission } };
        });
        return;
      }

      default:
    }
  };

  useEffect(() => {
    const companyId = user?.companyId;
    if (!companyId) return undefined;

    const socket = socketManager.getSocket(companyId);
    const onCallEvent = (data) => handleEventRef.current?.(data);
    socket.on(`company-${companyId}-call`, onCallEvent);

    return () => {
      socket.disconnect();
    };
  }, [socketManager, user?.companyId]);

  // Toque da ligação recebida (em loop). Em ligação, a lista aparece mas não toca.
  const inCall = Boolean(call && call.phase !== "ended");
  useEffect(() => {
    if (incomingCalls.length > 0 && !inCall) {
      if (!ringtoneRef.current) {
        ringtoneRef.current = new Audio(ringtoneSound);
        ringtoneRef.current.loop = true;
      }
      ringtoneRef.current.play().catch(() => {});
      return undefined;
    }

    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
    return undefined;
  }, [incomingCalls.length, inCall]);

  // Aviso esquecido (o "ended" se perdeu numa reconexão): some sozinho.
  useEffect(() => {
    if (!incomingCalls.length) return undefined;
    const timer = setInterval(() => {
      setIncomingCalls((prev) => prev.filter((item) => Date.now() - item.receivedAt < INCOMING_TIMEOUT_MS));
    }, 5000);
    return () => clearInterval(timer);
  }, [incomingCalls.length]);

  // Fechar/recarregar a aba no meio da ligação: pergunta antes e, se sair mesmo,
  // encerra na Meta (fetch com keepalive sobrevive ao descarregamento da página).
  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!callRef.current || callRef.current.phase === "ended") return;
      event.preventDefault();
      event.returnValue = "";
    };

    const onPageHide = () => {
      const current = callRef.current;
      if (!current?.callId || current.phase === "ended") return;

      let token = null;
      try {
        token = JSON.parse(localStorage.getItem("token"));
      } catch (err) {
        token = null;
      }

      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/calls/terminate`, {
          method: "POST",
          keepalive: true,
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ callId: current.callId }),
        });
      } catch (err) {
        // página já descarregando
      }
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);

  useEffect(
    () => () => {
      clearTimeout(endedTimerRef.current);
      ringtoneRef.current?.pause();
      releasePeer();
    },
    [releasePeer]
  );

  return (
    <CallContext.Provider
      value={{
        call,
        incomingCalls,
        permissionPrompt,
        canUseCalls,
        startCallFromTicket,
        startCallFromContact,
        answerIncoming,
        ignoreIncoming,
        hangUp,
        toggleMute,
        requestPermission,
        dialFromPrompt,
        endStaleCall,
        closePermissionPrompt,
      }}
    >
      {children}
      {/* Áudio do contato: fica no provider para sobreviver à troca de tela. */}
      <audio ref={remoteAudioRef} autoPlay playsInline style={{ display: "none" }} />
    </CallContext.Provider>
  );
};
